import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { carsService } from '../services/cars.service'
import { toast } from 'sonner'

export function useCarBrands() {
  return useQuery({
    queryKey: ['cars', 'brands'],
    queryFn: () => carsService.getBrands(),
  })
}

export function useCarModels(brandId?: number) {
  return useQuery({
    queryKey: ['cars', 'models', brandId],
    queryFn: () => (brandId ? carsService.getModels(brandId) : Promise.resolve([])),
    enabled: Boolean(brandId),
  })
}

export function useCarGenerations(modelId?: number) {
  return useQuery({
    queryKey: ['cars', 'generations', modelId],
    queryFn: () => (modelId ? carsService.getGenerations(modelId) : Promise.resolve([])),
    enabled: Boolean(modelId),
  })
}

export function useCarVariants(generationId?: number) {
  return useQuery({
    queryKey: ['cars', 'variants', generationId],
    queryFn: () => (generationId ? carsService.getVariants(generationId) : Promise.resolve([])),
    enabled: Boolean(generationId),
  })
}

export function useCarFeatures() {
  return useQuery({
    queryKey: ['cars', 'features'],
    queryFn: () => carsService.getFeatures(),
  })
}

export function useMarketplaceListings(params: any) {
  return useQuery({
    queryKey: ['cars', 'marketplace', params],
    queryFn: () => carsService.getMarketplaceListings(params),
  })
}

export function useCarDetails(slug: string) {
  return useQuery({
    queryKey: ['cars', 'details', slug],
    queryFn: () => carsService.getListingBySlug(slug),
    enabled: Boolean(slug),
  })
}

export function useMyCarListings(status?: string) {
  return useQuery({
    queryKey: ['cars', 'my-listings', status],
    queryFn: () => carsService.getMyListings(status),
  })
}

export function useCreateCarListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: any) => carsService.createListing(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cars', 'my-listings'] })
      queryClient.invalidateQueries({ queryKey: ['platform', 'billing', 'overview'] })
      toast.success('Car listing created successfully!')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err.message || 'Failed to create car listing')
    },
  })
}

export function useArchiveCarListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => carsService.archiveListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cars', 'my-listings'] })
      toast.success('Listing archived')
    },
  })
}

export function useUpdateCarListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: any }) => carsService.updateListing(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cars', 'my-listings'] })
      queryClient.invalidateQueries({ queryKey: ['cars', 'details'] })
      toast.success('Vehicle listing updated!')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err.message || 'Failed to update vehicle listing')
    },
  })
}

export function useRequestModel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: any) => carsService.requestModel(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'model-requests'] })
      toast.success('Vehicle model request submitted to Admin!')
    },
  })
}

export function useAdminCarListings(status?: string) {
  return useQuery({
    queryKey: ['admin', 'cars', 'listings', status],
    queryFn: () => carsService.adminGetListings(status),
  })
}

export function useAdminApproveListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => carsService.adminApproveListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'listings'] })
      toast.success('Listing approved & published!')
    },
  })
}

export function useAdminRejectListing() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => carsService.adminRejectListing(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cars', 'listings'] })
      toast.success('Listing rejected')
    },
  })
}

export function usePlatformUsage() {
  return useQuery({
    queryKey: ['platform', 'usage'],
    queryFn: () => carsService.getPlatformUsage(),
  })
}

export function usePlatformEntitlement() {
  return useQuery({
    queryKey: ['platform', 'entitlement'],
    queryFn: () => carsService.getPlatformEntitlement(),
  })
}

export function usePlatformSubscription() {
  return useQuery({
    queryKey: ['platform', 'subscriptions', 'me'],
    queryFn: () => carsService.getPlatformSubscription(),
  })
}
